import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { ClientFormWithCustomFields } from '@/features/public/booking/schemas/clientSchema.ts';

/**
 * Rozpoczęta rezerwacja jednego wyjazdu. Klient bywa zapisany na kilka naraz,
 * a formularze różnią się między nimi wszystkim poza danymi osobowymi.
 */
export interface BookingDraft {
  clientFormData: ClientFormWithCustomFields | undefined;
  promoCodes: string[];
  /**
   * Anonimowy identyfikator wizyty do zdarzeń lejka: bez danych osobowych,
   * bez powiązania z rezerwacją. Opcjonalny, bo szkice sprzed tej zmiany go
   * nie mają — dlatego `version` persist zostaje bez ruchu.
   */
  funnelSessionId?: string;
}

const EMPTY_DRAFT: BookingDraft = {
  clientFormData: undefined,
  promoCodes: [],
};

export interface BookingStoreProps {
  drafts: Record<string, BookingDraft>;
  setClientFormData: (
    tourKey: string,
    data: ClientFormWithCustomFields,
  ) => void;
  setPromoCodes: (tourKey: string, codes: string[]) => void;
  clearDraft: (tourKey: string) => void;
  /** Identyfikator wizyty, tworzony leniwie — przed pierwszym zdarzeniem lejka nie istnieje. */
  ensureFunnelSessionId: (tourKey: string) => string;
}

/**
 * Klucz szkicu. Slug organizatora wchodzi w skład, bo sam slug wyjazdu jest
 * unikalny tylko w obrębie firmy.
 */
export const bookingDraftKey = (orgSlug: string, tourSlug: string): string =>
  `${orgSlug}/${tourSlug}`;

/**
 * Rozpoczęte rezerwacje, po jednej na wyjazd.
 *
 * Podział na wyjazdy jest tu istotny, a nie porządkowy: szkic niesie
 * `selectedInsuranceId`, `selectedOptions` i `transportVariantId` — wybory
 * z katalogu **jednego** wyjazdu. Wspólny worek wprowadzał je do formularza
 * innego wyjazdu, gdzie klient ich nie widział i nie mógł odznaczyć, bo
 * odpowiednie sekcje w ogóle się nie renderowały, a zapis kończył się odmową
 * backendu z identyfikatorem, który nic klientowi nie mówił.
 */
export const useBookingStore = create<BookingStoreProps>()(
  persist(
    (set, get) => ({
      drafts: {},
      setClientFormData: (tourKey, data) =>
        set((state) => ({
          drafts: {
            ...state.drafts,
            [tourKey]: {
              ...(state.drafts[tourKey] ?? EMPTY_DRAFT),
              clientFormData: data,
            },
          },
        })),
      setPromoCodes: (tourKey, codes) =>
        set((state) => ({
          drafts: {
            ...state.drafts,
            [tourKey]: {
              ...(state.drafts[tourKey] ?? EMPTY_DRAFT),
              promoCodes: codes,
            },
          },
        })),
      clearDraft: (tourKey) =>
        set((state) => {
          const { [tourKey]: _removed, ...rest } = state.drafts;
          return { drafts: rest };
        }),
      ensureFunnelSessionId: (tourKey) => {
        const existing = draftOf(get(), tourKey).funnelSessionId;
        if (existing) return existing;

        const funnelSessionId = crypto.randomUUID();
        set((state) => ({
          drafts: {
            ...state.drafts,
            [tourKey]: {
              ...(state.drafts[tourKey] ?? EMPTY_DRAFT),
              funnelSessionId,
            },
          },
        }));
        return funnelSessionId;
      },
    }),
    {
      name: 'booking-storage',
      // Wersja 0 trzymała jeden szkic bez śladu, którego wyjazdu dotyczy —
      // czyli dokładnie ten stan, który psuł rezerwację. Nie ma go jak
      // przypisać do wyjazdu, więc migracja go porzuca.
      version: 1,
      migrate: () => ({ drafts: {} }),
    },
  ),
);

/** Szkic wyjazdu albo pusty — nigdy `undefined`, żeby selektory nie rozgałęziały się na to. */
export const draftOf = (
  state: BookingStoreProps,
  tourKey: string,
): BookingDraft => state.drafts[tourKey] ?? EMPTY_DRAFT;

export type BookingStore = ReturnType<typeof useBookingStore>;
